import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_SYMBOL, whatsappLink } from "../../utils/constants";
import { useLanguage } from "../../utils/i18n";

const navItems = {
  pt: [
    { label: "Início", path: "/" },
    { label: "Experiências", path: "/experiencias" },
    { label: "Oráculo", path: "/oraculo-oculto" },
    { label: "Mesa Radiónica", path: "/mesa-radionica" },
    { label: "Kundalini", path: "/kundalini" },
    { label: "Rituais", path: "/rituais" },
    { label: "João Ramos", path: "/sobre" },
    { label: "Contactos", path: "/contactos" },
  ],
  en: [
    { label: "Home", path: "/" },
    { label: "Experiences", path: "/experiencias" },
    { label: "Oracle", path: "/oraculo-oculto" },
    { label: "Radionic Table", path: "/mesa-radionica" },
    { label: "Kundalini", path: "/kundalini" },
    { label: "Rituals", path: "/rituais" },
    { label: "João Ramos", path: "/sobre" },
    { label: "Contact", path: "/contactos" },
  ],
  es: [
    { label: "Inicio", path: "/" },
    { label: "Experiencias", path: "/experiencias" },
    { label: "Oráculo", path: "/oraculo-oculto" },
    { label: "Mesa Radiónica", path: "/mesa-radionica" },
    { label: "Kundalini", path: "/kundalini" },
    { label: "Rituales", path: "/rituais" },
    { label: "João Ramos", path: "/sobre" },
    { label: "Contacto", path: "/contactos" },
  ],
  fr: [
    { label: "Accueil", path: "/" },
    { label: "Expériences", path: "/experiencias" },
    { label: "Oracle", path: "/oraculo-oculto" },
    { label: "Table Radionique", path: "/mesa-radionica" },
    { label: "Kundalini", path: "/kundalini" },
    { label: "Rituels", path: "/rituais" },
    { label: "João Ramos", path: "/sobre" },
    { label: "Contact", path: "/contactos" },
  ],
  br: [
    { label: "Início", path: "/" },
    { label: "Experiências", path: "/experiencias" },
    { label: "Oráculo", path: "/oraculo-oculto" },
    { label: "Mesa Radiônica", path: "/mesa-radionica" },
    { label: "Kundalini", path: "/kundalini" },
    { label: "Rituais", path: "/rituais" },
    { label: "João Ramos", path: "/sobre" },
    { label: "Contato", path: "/contactos" },
  ],
};

const ctaLabel = { pt: "Marcar Experiência", en: "Book Experience", es: "Reservar Experiencia", fr: "Réserver", br: "Agendar Experiência" };

const langLabels = { pt: "PT", en: "EN", es: "ES", fr: "FR", br: "BR" };

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const langRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const items = navItems[language] || navItems.pt;

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-[var(--black-deep)]/95 backdrop-blur-md border-b border-[var(--border-subtle)] py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_SYMBOL} alt="Sophitris" className={`transition-all duration-300 logo-glow ${isScrolled ? "h-9" : "h-11"}`} />
            <span className={`font-semibold tracking-[0.2em] text-[var(--gold)] uppercase transition-all duration-300 ${isScrolled ? "text-sm" : "text-base"}`} style={{ fontFamily: "var(--font-body)" }}>SOPHITRIS</span>
          </Link>

          <div className="hidden xl:flex items-center gap-6">
            {items.map((item) => (
              <Link key={item.path} to={item.path}
                className={`text-xs tracking-[0.1em] uppercase transition-colors duration-300 ${location.pathname === item.path ? "text-[var(--gold)]" : "text-[var(--text-secondary)] hover:text-[var(--ivory)]"}`}>
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <div ref={langRef} className="relative">
              <button onClick={() => setLangOpen(!langOpen)} className="text-xs tracking-wider text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors uppercase" data-testid="lang-selector">
                {langLabels[language]}
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 bg-[var(--black-graphite)] border border-[var(--border-subtle)] py-1 min-w-[48px]">
                  {Object.entries(langLabels).map(([code, label]) => (
                    <button key={code} onClick={() => { setLanguage(code); setLangOpen(false); }}
                      className={`block w-full px-3 py-1.5 text-xs text-left transition-colors ${language === code ? "text-[var(--gold)]" : "text-[var(--text-secondary)] hover:text-[var(--ivory)]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a href={whatsappLink("default")} target="_blank" rel="noopener noreferrer"
              className="hidden lg:inline-flex items-center justify-center gap-2 py-2.5 px-5 bg-[var(--ruby)] text-[var(--ivory)] text-[0.6875rem] font-semibold tracking-[0.12em] uppercase border-none cursor-pointer transition-all hover:bg-[var(--ruby-bright)]"
              data-testid="nav-cta">
              {ctaLabel[language] || ctaLabel.pt}
            </a>

            <button onClick={() => setMobileOpen(!mobileOpen)} className="xl:hidden text-[var(--ivory)] p-1" data-testid="mobile-menu-toggle" aria-label="Menu">
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-[var(--black-deep)] pt-20 px-6 overflow-y-auto animate-fade-in" data-testid="mobile-menu">
          <div className="flex flex-col gap-1 mb-8">
            {items.map((item) => (
              <Link key={item.path} to={item.path}
                className={`py-3 text-lg border-b border-[var(--border-subtle)] transition-colors ${location.pathname === item.path ? "text-[var(--gold)]" : "text-[var(--text-secondary)]"}`}
                style={{ fontFamily: "var(--font-heading)" }}>
                {item.label}
              </Link>
            ))}
          </div>
          <a href={whatsappLink("default")} target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center">
            {ctaLabel[language] || ctaLabel.pt}
          </a>
        </div>
      )}
    </>
  );
};
